/**
 * Plan 119 Phase 2 (FS-460) — equipment vocabulary, in words.
 *
 * The ladder used to live here: the client looked at the source, the
 * component and the capacity source and worked out what a value was worth.
 * That made the Tonnage Quality column impossible to sort — there was no
 * server column to order by — and it meant the ladder existed twice, once in
 * this file and once in whatever the analysis did.
 *
 * The server now computes a quality code per field (device-data-api
 * `src/equipment/quality.ts`, mirrored as SQL so the Device List can sort and
 * filter on it). This file only turns those codes into the sentence an
 * operator reads. No judgement is made here.
 *
 * Copy rules: plain English, and an absent value says so rather than leaving
 * a blank cell.
 */

import type { EquipmentDocumentRef } from '../types';
import { own } from './ownKey';

/** `fieldSources[*].source` as the app writes it. */
export type ProvenanceSource =
  | 'user'
  | 'ai_photo'
  | 'ai_model_decode'
  | 'derived'
  | 'inferred'
  | 'behavioral'
  // Plan 119 Phase 3 (FS-627): the resolver read the value out of a
  // manufacturer document held in the equipment model catalog.
  | 'spec_lookup';

/** `equipmentSide` — which component of the system the record describes. */
export type EquipmentSide =
  | 'outdoor_condenser'
  | 'indoor_air_handler'
  | 'indoor_coil'
  | 'package_unit';

type Maybe = string | null | undefined;

const SOURCE_LABELS: Record<string, string> = {
  user: 'user entry',
  ai_photo: 'label photo',
  ai_model_decode: 'model number',
  derived: 'calculation',
  inferred: 'inference',
  behavioral: 'observed running',
  spec_lookup: 'manufacturer document',
  // FS-752: an operator's correction from the console. The model number's
  // provenance is the first column to show it, and "unknown" would read as a
  // record nobody can account for.
  admin: 'operator entry',
};

/** Short plain-English name for where a value came from. */
export function sourceLabel(source: Maybe): string {
  return own(SOURCE_LABELS, source) || 'unknown';
}

/**
 * Server quality code → the words for it. Keep this in step with
 * device-data-api `src/equipment/quality.ts`; the codes are the contract.
 */
const QUALITY_LABELS: Record<string, string> = {
  // Tonnage, version 9 (FS-835), best first. The server's ladder describes
  // the resolver's winner: which unit it came off (indoor unit, then indoor
  // coil, then outdoor unit) and on what evidence. A disagreement does not
  // move the rung; it is its own field (`app_hvac_tonnage_conflict`). The
  // `_unattributed` rungs are values the app wrote before it recorded where a
  // fact came from, one rung below their attributed twins.
  tonnage_indoor_unit_document: 'manufacturer document for the indoor unit',
  tonnage_indoor_unit_user: 'entered by a person on the indoor unit',
  tonnage_indoor_unit_nameplate: 'label photo on the indoor unit',
  tonnage_indoor_unit_nameplate_unattributed: 'indoor unit nameplate, origin not recorded',
  tonnage_indoor_unit_decode: 'indoor unit model number',
  tonnage_indoor_unit_decode_unattributed: 'indoor unit model decode, origin not recorded',
  tonnage_coil_user: 'entered by a person on the indoor coil',
  tonnage_coil_document: 'manufacturer document for the indoor coil',
  tonnage_coil_nameplate: 'label photo on the indoor coil',
  tonnage_coil_nameplate_unattributed: 'indoor coil nameplate, origin not recorded',
  tonnage_coil_decode: 'indoor coil model number',
  tonnage_coil_decode_unattributed: 'indoor coil model decode, origin not recorded',
  tonnage_outdoor_user: 'entered by a person on the outdoor unit',
  tonnage_outdoor_document: 'manufacturer document for the outdoor unit',
  tonnage_outdoor_nameplate: 'label photo on the outdoor unit',
  tonnage_outdoor_nameplate_unattributed: 'outdoor unit nameplate, origin not recorded',
  tonnage_outdoor_decode: 'outdoor unit model number',
  tonnage_outdoor_decode_unattributed: 'outdoor unit model decode, origin not recorded',
  tonnage_derived_btu: 'worked back from a cooling BTU rating',
  tonnage_inferred: 'inferred',
  tonnage_source_unknown: 'on file, source unknown',
  // The last resort: a furnace or air handler's "up to N tons", used only
  // when nothing else on any component gives a size.
  tonnage_indoor_unit_maximum: 'upper bound only: the indoor unit\'s "up to" size',
  tonnage_ductless_unresolved: 'ductless head on a larger outdoor unit; no system size taken',
  tonnage_missing: 'not on file',
  tonnage_unavailable: 'record could not be read',
  // Retired at version 9; kept for older exports and saved filters.
  tonnage_user: 'entered by a person',
  tonnage_catalog_document: 'manufacturer document for the model',
  tonnage_coil_assumed: 'from the coil',
  tonnage_coil_assumed_unattributed: 'from the coil, origin not recorded',
  tonnage_air_handler_decode: 'air handler model number',
  tonnage_air_handler_decode_unattributed: 'air handler model decode, origin not recorded',
  tonnage_component_unknown: 'read from an unrecorded part',
  tonnage_component_unknown_unattributed: 'model decode, part and origin not recorded',

  // Motor family, best first. `unknown` is the extraction saying it could not
  // tell, which must never read as a decode that succeeded.
  motor_catalog_document: 'manufacturer document',
  // A homeowner said one thing and the manufacturer document says another.
  // Neither value is acted on until a person settles it, so this rung reads
  // as a disagreement rather than as a value.
  motor_user_vs_catalog_conflict: 'homeowner value disagrees with the manual',
  motor_user: 'entered by a person',
  motor_label_marking: 'marking on a label photo',
  // Plan 119 Phase 6 (version 4): a motor family decides every airflow call
  // this product makes, so a decode is only worth consuming when something
  // else agrees with it. The two rungs must never read alike — the whole
  // point of the split is that one of them may not be acted on.
  motor_model_decode_confirmed: 'model number, checked against a label photo',
  motor_model_decode_unconfirmed: 'model number only, nothing confirms it',
  // The pre-split code. The server stopped emitting it at version 4, but rows
  // written before then are still in the inventory and in exports taken from
  // it, so it keeps its words rather than rendering as a raw code.
  motor_model_decode: 'model number',
  motor_behavioral: 'observed running',
  motor_source_unknown: 'on file, source unknown',
  motor_legacy_value: 'old value, not yet migrated',
  motor_unknown_family: 'could not be determined',
  // Plan 120 (version 5): the only indoor component on file is an evaporator
  // coil, which has no blower. Deliberately not "not on file" — we know why
  // there is no motor family and what would produce one, and deliberately not
  // "no blower on this system", which is what `not_applicable` claims.
  motor_coil_only: 'coil only; the furnace or air handler has not been photographed',
  motor_missing: 'not on file',
  motor_unavailable: 'record could not be read',
  motor_not_applicable: 'no blower on this system',

  // Filter curve, best first. Plan 119 Phase 4 splits the old "filter known,
  // no curve" rung by WHO said what the filter is: a label reading is evidence
  // the back end can turn into a catalog row, a person's entry is not. The two
  // rungs below them are what is left when nothing recorded a source at all.
  filter_certified_exact: 'certified curve, exact size',
  // FS-686 (version 6): a certified curve at the right size whose product the
  // matcher could not pin down, because several products share the brand, size
  // and rating or the recorded model matched none. Below the exact rung, and
  // it no longer claims "exact size" for a curve that may be the wrong product.
  filter_certified_unresolved: 'certified curve, product not confirmed',
  // FS-752 (version 7) split the catalog rung by what the curve IS. A
  // certified curve whose size nobody confirmed is not filter information we
  // can stand behind; a curve somebody typed in by hand is. The two must
  // never read alike, because only one of them leaves Missing info.
  filter_certified_size_unconfirmed: 'certified curve, size not confirmed',
  filter_catalog_hand_entered: 'hand-entered curve',
  // The pre-split code. The server stopped emitting it at version 7, but rows
  // and exports taken before then still carry it, so it keeps its words.
  filter_catalog_uncalibrated: 'catalog curve, size unconfirmed',
  filter_extrapolated: 'estimated curve',
  filter_identity_photo: 'read from the label photo, no curve yet',
  filter_identity_user: 'entered by a person, no curve',
  filter_photo_no_match: 'photo on file, no match yet',
  filter_identity_only: 'filter known, source not recorded',
  filter_missing: 'not on file',
  filter_unavailable: 'record could not be read',

  // Rated external static pressure. Phase 3 (FS-627) gave this field a rung
  // above "not on file": the equipment model catalog holds the manufacturer's
  // published operating point, with the document behind it.
  rated_static_document: 'manufacturer document',
  rated_static_missing: 'not on file',
  // Phase 6 (version 4): the figure is read off the projection, so a device
  // whose nightly refresh has been failing may be holding one the catalog has
  // since corrected — or one attached to a model it no longer resolves to.
  rated_static_unavailable: 'record could not be read',

  // The photo asks. Each has a third rung at version 4: the counts come off
  // the same projection, so a stale device cannot say whether a photo was
  // taken since. "Nobody has taken one" and "we cannot see whether anybody
  // has" are different answers, and only the first is worth a nudge.
  outdoor_photo_on_file: 'on file',
  outdoor_photo_missing: 'not taken',
  outdoor_photo_unavailable: 'could not be checked',
  hvac_photo_on_file: 'on file',
  // Plan 120 (version 5): a photo we HAVE and could not place on a component.
  // The analysis came back unresolved or in conflict with itself, so nothing
  // was promoted from it. Never "not taken" — one was.
  hvac_photo_unclassified: 'on file, could not be placed on a component',
  hvac_photo_missing: 'not taken',
  hvac_photo_unavailable: 'could not be checked',
  filter_photo_on_file: 'on file',
  filter_photo_missing: 'not taken',
  filter_photo_unavailable: 'could not be checked',

  // Plan 120 (version 5) — the equipment RECORD's own row. Not a fact about
  // the system: an answer to "can these component records be read at face
  // value", which the legacy guard can hold while an operator settles where a
  // mis-slotted identity belongs.
  hvac_component_resolved: 'nothing held for review',
  hvac_component_review: 'held for review',
  hvac_component_unavailable: 'could not be checked',

  // FS-752 (version 7) — the furnace or air handler RECORD, best first. Not a
  // photo: a model number the equipment catalog resolved to an indoor unit,
  // with a manufacturer document behind it. A person having reviewed the
  // catalog row is shown as the better rung, never required. Each rung says
  // what is missing in the words a tech would use on the phone.
  indoor_record_reviewed: 'model and manual confirmed, reviewed by a person',
  indoor_record_documented: 'model and manual confirmed',
  indoor_record_no_document: 'model known, no manual on file',
  indoor_record_type_unconfirmed: 'model known, equipment type not confirmed',
  indoor_record_no_source: 'real model, manufacturer information not found',
  // FS-786 (version 8): the research found the document and kept no fact.
  indoor_record_document_no_facts: 'model known, manual found, no facts read from it yet',
  indoor_record_ambiguous: 'model matches more than one product',
  indoor_record_pending: 'model waiting for lookup',
  indoor_record_not_a_model: 'entry is not a model number',
  indoor_record_wrong_component: 'model belongs to another unit',
  indoor_record_coil_only: 'only the coil is on file',
  indoor_record_held: 'held for review',
  indoor_record_missing: 'no model number on file',
  indoor_record_not_applicable: 'no separate furnace or air handler',
  indoor_record_unavailable: 'record could not be read',
};

/**
 * The words for a server quality code. A code nobody has a label for is shown
 * exactly as the server sent it — a value nobody expected is worth seeing.
 */
export function qualityLabel(code: Maybe): string {
  if (!code) return 'unknown';
  return own(QUALITY_LABELS, code) ?? code;
}

/** The fact states, in words. */
const STATE_LABELS: Record<string, string> = {
  known: 'On file',
  weak: 'On file, weak',
  // Plan 119 Phase 3 (FS-627): a value is recorded AND contradicted. It is
  // deliberately not "on file" — nothing downstream should use it until a
  // person decides which side is right.
  conflict: 'Disagrees with the manual',
  missing: 'Missing',
  unknown: 'Could not be determined',
  not_applicable: 'Not applicable',
  unavailable: 'Record could not be read',
};

export function stateLabel(state: Maybe): string {
  if (!state) return 'unknown';
  return STATE_LABELS[state] ?? state;
}

/**
 * What happens next, and who does it.
 *
 * Plan 123 Phase 4b: every label names an actor. "We can work it out" read as
 * encouragement rather than as a queue, so `back_end_recoverable` is now
 * "Waiting on us" and `ops_contact` is "Needs a look" — the same two phrases
 * the Missing tab puts on a blocked component, so one row cannot be described
 * two ways on two screens.
 */
const ROUTE_LABELS: Record<string, string> = {
  none: 'Nothing to do',
  back_end_recoverable: 'Waiting on us',
  nudge_user: 'Ask the homeowner',
  ops_contact: 'Needs a look',
  not_applicable: 'Not applicable',
};

/** One line under the label, for a screen with room to explain the route. */
const ROUTE_DESCRIPTIONS: Record<string, string> = {
  none: 'Nothing is needed from anybody',
  back_end_recoverable:
    'Evidence is on file; a FluxHive step comes next, not the homeowner',
  nudge_user: 'Only the homeowner can settle this, so we ask them for a photo',
  ops_contact: 'A person at FluxHive has to look at this one and decide',
  not_applicable: 'This system has no such part, so nobody acts',
};

export function routeLabel(route: Maybe): string {
  if (!route) return 'unknown';
  return ROUTE_LABELS[route] ?? route;
}

/** The sentence under a route label. Empty for a route nobody has words for. */
export function routeDescription(route: Maybe): string {
  if (!route) return '';
  return ROUTE_DESCRIPTIONS[route] ?? '';
}

/**
 * Why a row needs attention, spelled out. No colour-only signal: the reason
 * is words, and any tint only repeats what the words already say.
 *
 * This lives here rather than on the Equipment Inventory page because the
 * device Overview says the same things about a missing rated pressure, and
 * the two screens must not word it differently (Plan 119 Phase 3, FS-627).
 */
export const ROUTE_REASON_LABELS: Record<string, string> = {
  on_file: 'on file',
  photo_on_file: 'photo on file',
  fact_already_established: 'already established, do not ask',
  // Plan 119 Phase 6 (version 4). This one is not a gap in what the homeowner
  // told us — it is the nightly job failing on this device, so every fact on
  // the row is as old as the projection behind it. How old is in the row's
  // evidence; the reason says whose problem it is.
  projection_stale:
    "the nightly refresh of this device's equipment summary is failing, so these facts are out of date",
  // A decode nothing checked. The decoder has already had this model number,
  // so asking it again is a route to nowhere; a photo of the furnace label is
  // the one thing that would settle it.
  motor_decode_unconfirmed:
    'the motor type was decoded from the model number alone; a photo of the furnace label would confirm it',
  no_blower_bearing_unit: 'this system has no blower',
  // Phase 3 replaced the single "needs a manufacturer lookup" placeholder
  // with the two answers the equipment model catalog can actually give, plus
  // the homeowner-versus-document disagreement. They are different pieces of
  // work: one is research, the other is a phone call.
  user_vs_catalog: 'the homeowner value disagrees with the manufacturer document',
  catalog_has_no_rated_esp: 'the catalog has no rated pressure for this model',
  model_not_in_catalog: 'this model is not in our catalog',
  // Plan 123 Phase 4b: an operator reads these, and "claim" is our word for
  // the row, not theirs. Say what it is: an app account registered to a Clip.
  claim_missing: 'no app account is registered to this device',
  claim_ambiguous: 'more than one app account is registered to this device',
  claim_stale: 'the app account registered to this device is out of date',
  claim_not_current: 'the app account registered to this device is not the current one',
  // A value is on file that nothing on the record can rank, and the resolver
  // has already had its go — a second pass writes the same unattributed
  // number. Only a fresh photo or a person confirming the figure settles it.
  recapture_or_confirm: 'ask for a new photo, or confirm with the homeowner',
  ac_model_decode: 'decode the A/C model number',
  hvac_model_decode: 'decode the HVAC model number',
  // Plan 123 R7. A model number is our work only until the equipment catalog
  // has a verdict on it; once it has one, no second lookup changes anything
  // and the label in the homeowner's hand is the only thing left.
  model_not_a_model: 'not a model number; ask for a label photo',
  model_decode_exhausted: 'looked up and not settled; ask for a label photo',
  // Plan 123 R7. The three `*_photo_on_file` reasons used to sit here, and
  // they described a job nothing does: the app reads a label at upload and no
  // back-end pass ever revisits a stored photo. A photo beside a fact that is
  // still missing was therefore already read and gave nothing.
  photo_unreadable: 'the photo on file gave nothing; ask for a clearer one',
  legacy_value_migration: 'migrate the old value',
  catalog_match_work: 'match it to a catalog curve',
  // Plan 119 Phase 4: the label was READ, so there is an identity to propose a
  // catalog row for rather than an image for somebody to look at.
  catalog_proposal_from_photo: 'propose a catalog row from the label photo',
  no_owner_email: 'nobody to write to',
  ask_outdoor_photo: 'ask for a photo of the outdoor unit',
  ask_hvac_photo: 'ask for a photo of the furnace or air handler',
  ask_filter_photo: 'ask for a photo of the filter label',
  // FS-838: a furnace's label gives only an "up to" size, so the indoor coil
  // is asked for the system's size before the outdoor unit.
  ask_coil_photo: 'ask for a photo of the indoor coil label',
  // Plan 120. A photo is on file and the analysis could not say which
  // component it shows, so nothing was promoted from it. Asking the homeowner
  // for another one is FS-573's job, not this row's.
  photo_unclassified:
    'a photo on file could not be placed on a component, so nothing was read from it',
  // Plan 123 §4.1: the record says the system has no such part, and also
  // holds a fact or a photo for it. Both cannot be true.
  applicability_conflict:
    'this part is declared absent, but the record still holds evidence of it',
  // The three holds the legacy guard records, each a different piece of work.
  // They travel as route reasons rather than as three codes so a new hold does
  // not need a new code and a client version bump.
  legacy_guard_pending:
    'an old-build write to this record has not been processed by the legacy guard yet',
  legacy_identity_held:
    'an identity that belonged to another component was moved out of the HVAC slot and kept for review',
  typed_coil_model_on_parent:
    'the indoor unit carries a typed model number that the catalog reads as an evaporator coil',
  shared_record_multiple_filters: 'shared record: two filter sizes on one system',
  // FS-752 — why the furnace or air handler record is not complete yet. The
  // first three are FluxHive's catalog work; the last two need a person.
  catalog_document_missing: 'the model is known; we have no manufacturer document for it yet',
  catalog_type_unconfirmed: 'the model is known; the catalog does not say what kind of unit it is',
  catalog_no_source: 'the model is real; no manufacturer information has been found for it',
  // FS-786: the document is in our store; what is left is reading it again.
  catalog_document_no_facts:
    'we found the manufacturer document for this model and have not read a fact from it yet',
  model_ambiguous: 'the model number matches more than one product',
  model_wrong_component: 'the model on the furnace record belongs to another unit',
  no_evidence: 'nothing to work from',
};

/**
 * The words for a route reason. A reason with no entry falls back to
 * underscore-stripping, which is legible but reads like a code, so the
 * coverage test treats that as missing.
 */
export const routeReasonLabel = (reason: Maybe): string => {
  if (!reason) return 'unknown';
  return own(ROUTE_REASON_LABELS, reason) ?? reason.replace(/_/g, ' ');
};

/**
 * Plan 119 Phase 4 — who established what the filter IS, in words.
 *
 * The server sends one answer (`filter_identity_source`), computed the same
 * way the ladder computes it: a label reading beats a person's entry, because
 * it is the one that carries evidence somebody can act on. Null is a record
 * written before the app said where its facts came from, and it says so rather
 * than reading as a weak source.
 */
const FILTER_IDENTITY_SOURCES: Record<string, string> = {
  ai_photo: 'identity from the label photo',
  user: 'entered by the homeowner',
  admin: 'entered by an operator',
};

export function filterIdentitySourceLabel(source: Maybe): string {
  return own(FILTER_IDENTITY_SOURCES, source) || 'source not recorded';
}

/**
 * The catalog match behind a filter curve, in one line:
 * `catalog match: certified (maedb), matched 2026-09-11`.
 *
 * `source` is the source the match PINNED, not what the catalog row says now —
 * the API sends the pin, because it is what the grade on this device was
 * computed from. With no match at all the line says so; it is never blank.
 */
export function catalogMatchLabel(f: {
  catalogId?: number | null;
  source?: Maybe;
  confidence?: Maybe;
  sizeVerified?: boolean | null;
  matchedAt?: Maybe;
}): string {
  if (f.catalogId == null) return 'no catalog match';
  const grade = f.confidence === 'high' ? 'certified' : f.confidence === 'low' ? 'uncalibrated' : null;
  const parts = [
    grade && f.source ? `${grade} (${f.source})` : grade ?? (f.source || null),
    f.sizeVerified === true ? 'size verified' : f.sizeVerified === false ? 'size unconfirmed' : null,
    f.matchedAt ? `matched ${f.matchedAt.slice(0, 10)}` : null,
  ].filter((p): p is string => !!p);
  return parts.length > 0 ? `catalog match: ${parts.join(', ')}` : 'catalog match on file';
}

/** `app_hvac_rated_esp_kind` in words. */
const RATED_STATIC_KINDS: Record<string, string> = {
  rating_point: 'rating point',
  furnace_limit: 'furnace limit',
};

export interface RatedStaticFacts {
  /** `app_hvac_rated_esp_pa` — the converted number, and the presence test. */
  pa?: number | null;
  /** `app_hvac_rated_esp_original` — the manufacturer's own wording. */
  original?: string | null;
  /** `app_hvac_rated_esp_kind` — a rating point or a furnace limit. */
  kind?: string | null;
  /**
   * The document behind the fact. `undefined` means the caller never looked
   * one up (the Device List row carries no document title), and the clause is
   * left off; `null` means the caller looked and there is none, which is
   * stated.
   */
  document?: EquipmentDocumentRef | null;
  /** `hvac_rated_esp_locator` — where in the document, e.g. `p.6`. */
  locator?: string | null;
  /**
   * `app_hvac_catalog_model_id` — what an absent value is explained by. A
   * string as well as a number: the device search sends the bigint as text.
   * Only ever read for whether there is one.
   */
  catalogModelId?: number | string | null;
}

/**
 * The one formatter for a rated external static pressure, so the device
 * Overview and the Device List cannot describe the same value differently.
 *
 * With a value: `0.5 in. w.c. (rating point) — Lennox EHB CBA25UH p.6`.
 * Without one, it says why — the two reasons are the same words the server's
 * route reason carries, because they are literally read from the same map.
 */
export function ratedStaticLabel(f: RatedStaticFacts): string {
  if (f.pa === null || f.pa === undefined) {
    const why = f.catalogModelId
      ? routeReasonLabel('catalog_has_no_rated_esp')
      : routeReasonLabel('model_not_in_catalog');
    return `not on file — ${why}`;
  }

  const value = f.original?.trim() || `${f.pa} Pa`;
  const kind = f.kind ? RATED_STATIC_KINDS[f.kind] ?? f.kind : 'kind not recorded';
  const head = `${value} (${kind})`;

  if (f.document === undefined) return head;
  return `${head} — ${documentReferenceLabel(f.document, f.locator)}`;
}

/**
 * A manufacturer document named the way a person would cite it:
 * `Lennox EHB CBA25UH rev 2019 p.6`. A null document says so rather than
 * rendering an empty reference.
 */
export function documentReferenceLabel(
  document: EquipmentDocumentRef | null | undefined,
  locator?: Maybe,
): string {
  if (!document) return 'no document on file';
  return [
    document.title?.trim() || 'untitled document',
    document.revision ? `rev ${document.revision}` : '',
    locator?.trim() || '',
  ]
    .filter((part) => part.length > 0)
    .join(' ');
}

/**
 * A homeowner's motor entry against the manufacturer document, in one
 * sentence. Both halves use the console's own motor names.
 *
 * `catalogValue` is the motor family the document states. It is null when no
 * catalog fact sits behind the conflict, and then the sentence says the
 * document disagrees rather than quoting a value nobody has.
 */
export function motorConflictLabel(userValue: Maybe, catalogValue?: Maybe): string {
  const entered = `homeowner entered ${motorTypeLabel(userValue)}`;
  if (!catalogValue) {
    return `${entered}; the manufacturer document for this model says something different`;
  }
  return `${entered}; manufacturer document says ${motorTypeLabel(catalogValue)}`;
}

/**
 * FS-541 — the one "Missing" set Randy asked for on 2026-08-24, narrowed by
 * FS-752 to the four things an HVAC tech means by "missing information": the
 * cooling capacity, the furnace or air handler record, the blower motor type
 * and the filter. Photos are evidence, not information, so they left the set.
 *
 * The same map names the equipment inventory's fields, which is why the old
 * keys stay: inventory rows still carry `filter_curve` and the photo fields,
 * and a Device List export taken before FS-752 still has the photo keys in it.
 */
const MISSING_FIELD_LABELS: Record<string, string> = {
  // The four FS-752 keys, in the order the server lists them.
  tonnage: 'Cooling capacity (tons)',
  indoor_record: 'Furnace or air handler information',
  motor_type: 'Blower motor type',
  filter_record: 'Filter information',
  // Inventory fields, and the pre-FS-752 Missing info keys.
  filter_curve: 'Filter curve',
  rated_static: 'Rated static pressure',
  outdoor_photo: 'Outdoor unit photo',
  hvac_photo: 'Furnace or air handler photo',
  filter_photo: 'Filter photo',
  // Plan 120 — the equipment record's own row in the inventory.
  hvac_component: 'Equipment record review',
};

export function missingFieldLabel(field: Maybe): string {
  if (!field) return 'unknown';
  return MISSING_FIELD_LABELS[field] ?? field;
}

/**
 * FS-752 — the Missing info keys the server sends today, in its order. The
 * Device List's option list comes from the server facet; this is what the
 * tests and the saved-filter cleanup hold the client to.
 */
export const MISSING_INFO_KEYS = ['tonnage', 'indoor_record', 'motor_type', 'filter_record'] as const;

/**
 * Missing info keys the server no longer sends. A saved filter naming one of
 * them matches nothing, so it is dropped on load — never mapped to a new key,
 * because a photo filter and a record filter answer different questions.
 */
export const RETIRED_MISSING_INFO_KEYS: readonly string[] = [
  'outdoor_photo',
  'hvac_photo',
  'filter_photo',
  'filter_curve',
];

/**
 * The server's comma-joined list as one readable line. Never a blank cell.
 *
 * FS-752: each key carries its reason when the caller has the field's quality
 * code — "Filter information (certified curve, size not confirmed)" — so the
 * cell says WHY, not only what. A key with no code beside it (an old export,
 * an API that predates the field) prints its name alone.
 */
export function missingFieldsLabel(
  fields: Maybe,
  reasons: Partial<Record<string, Maybe>> = {},
): string {
  const parts = (fields ?? '').split(',').map((f) => f.trim()).filter((f) => f.length > 0);
  if (parts.length === 0) return 'Nothing missing';
  return parts
    .map((f) => {
      const code = reasons[f];
      return code ? `${missingFieldLabel(f)} (${qualityLabel(code)})` : missingFieldLabel(f);
    })
    .join(', ');
}

/**
 * FS-752 — who put the indoor model number on the record. Shown beside the
 * record, never part of whether it counts. Null is a record written before
 * the app said where its model number came from, and it says so; a source
 * this bundle has no words for is shown as sent.
 */
export function modelSourceLabel(source: Maybe): string {
  if (!source) return 'not recorded';
  return own(SOURCE_LABELS, source) ?? source;
}

/**
 * FS-752 — whether the current filter's airflow record is calibrated. A
 * separate question from Missing info: a system can have every fact on file
 * and still be waiting for a calibrated day.
 */
const AIRFLOW_CALIBRATION_LABELS: Record<string, string> = {
  calibrated: 'Calibrated',
  not_calibrated: 'Not calibrated',
  no_record: 'No airflow record yet',
};

export function airflowCalibrationLabel(value: Maybe): string {
  if (!value) return 'unknown';
  return AIRFLOW_CALIBRATION_LABELS[value] ?? value;
}

const MOTOR_TYPES: Record<string, string> = {
  psc: 'PSC',
  ecm_constant_torque: 'constant-torque ECM',
  ecm_constant_airflow: 'constant-airflow ECM',
  shaded_pole: 'shaded-pole',
  unknown: 'unknown',
  // Values written before the vocabulary was settled. Both are still in
  // production: `multi_speed` describes speed taps rather than a motor family,
  // so it is shown as recorded and not translated into one.
  ecm_variable: 'variable-speed ECM (legacy value)',
  multi_speed: 'multi-speed (legacy value)',
};

/**
 * Motor type as a person would say it. An unrecognised value is shown exactly
 * as stored rather than hidden — a value nobody expected is worth seeing.
 */
export function motorTypeLabel(value: Maybe): string {
  if (!value) return 'unknown';
  return MOTOR_TYPES[value] ?? value;
}

/**
 * Plan 120 — what each component card is called.
 *
 * The Overview used to show one card headed "HVAC system" over a record that
 * could be a furnace, an air handler, a packaged unit or — on ten production
 * devices — an evaporator coil with a furnace's blower type stapled to it.
 * A heading that names the machine is the difference between a record an
 * operator can read and one they have to decode, so the heading is derived
 * from what the catalog actually resolved and says "unconfirmed" the moment
 * it cannot name the component.
 *
 * Never guess. `unknown`, `indoor_coil` and every outdoor type on the indoor
 * record all mean the same thing here: nobody has established what the indoor
 * unit is.
 */
const INDOOR_UNIT_TYPE_NAMES: Record<string, string> = {
  furnace: 'Furnace',
  air_handler: 'Air handler',
  package_unit: 'Packaged unit',
};

/** The catalog could not name the indoor unit, and there is a record for one. */
export const INDOOR_UNIT_UNCONFIRMED = 'Indoor unit — type unconfirmed';

/**
 * A coil is on file and no indoor unit is. Worded as an absence rather than as
 * an unknown type: the coil is not a unit of unknown type, it is a component
 * that has no blower, and the machine that does has not been photographed.
 */
export const INDOOR_UNIT_COIL_ONLY = 'Furnace or air handler not confirmed';

/** The coil's own card. "(A-coil)" is what a tech calls it on the phone. */
export const COIL_CARD_TITLE = 'Indoor coil (A-coil)';

/** The coil card's model line when the coil child carries no model number. */
export const COIL_MODEL_UNCONFIRMED = 'Model not confirmed';

export function indoorUnitCardTitle(f: {
  catalogEquipmentType?: string | null;
  model?: string | null;
  coilCount?: number | null;
}): string {
  const named = f.catalogEquipmentType
    ? INDOOR_UNIT_TYPE_NAMES[f.catalogEquipmentType]
    : undefined;
  if (named) return named;
  const hasIdentity = !!(f.model && f.model.trim().length > 0);
  if (!hasIdentity && (f.coilCount ?? 0) > 0) return INDOOR_UNIT_COIL_ONLY;
  return INDOOR_UNIT_UNCONFIRMED;
}

/**
 * The outdoor card. "Outdoor unit" is always true of it; the catalog type is
 * added when there is one, because a condenser and a heat pump are not the
 * same machine and the record often does not say which it is.
 */
const OUTDOOR_UNIT_TYPE_NAMES: Record<string, string> = {
  outdoor_condenser: 'condenser',
  heat_pump: 'heat pump',
  package_unit: 'packaged unit',
};

export function outdoorUnitCardTitle(catalogEquipmentType?: string | null): string {
  const named = catalogEquipmentType
    ? OUTDOOR_UNIT_TYPE_NAMES[catalogEquipmentType]
    : undefined;
  return named ? `Outdoor unit — ${named}` : 'Outdoor unit';
}

/**
 * Plan 120 — the hold the legacy guard placed on this equipment record, in a
 * few words, for a badge. The full sentence is the route reason; this is what
 * fits beside a heading.
 */
const COMPONENT_REVIEW_BADGES: Record<string, string> = {
  legacy_guard_pending: 'old write not processed yet',
  legacy_identity_held: 'identity held for review',
  typed_coil_model_on_parent: 'typed model reads as a coil',
};

export function componentReviewBadgeLabel(reason: Maybe): string | null {
  if (!reason) return null;
  return own(COMPONENT_REVIEW_BADGES, reason) ?? reason.replace(/_/g, ' ');
}
